/* eslint-disable react/destructuring-assignment */
import React, { useContext, useEffect, useState } from 'react';
import * as carouselComponents from 'pure-react-carousel';
import 'pure-react-carousel/dist/react-carousel.es.css';

import Icon from '../../atoms/Icon';
import { QuoteListProps, DotsComponentProps, QuoteSlidesProps } from './QuoteList.types';
import styles from './QuoteList.module.scss';
import QuoteItem from '../../molecules/QuoteItem';
import useCheckMobileScreen from '../../shared/utils/checkMobileScreen';
import IconProps from '../../atoms/Icon/Icon.types';

const chevronIcon: IconProps = {
  width: '14',
  height: '8',
  fill: '#ffff',
  icon: 'chevron',
  viewBox: '0 0 14 8',
  title: 'Arrow',
};

const { CarouselContext, CarouselProvider, Slider, Slide, Dot } = carouselComponents;
const oneSlide = 1;
const threeSlide = 3;
const visibleSlides = (isMobile) => (isMobile ? oneSlide : threeSlide);
const isOdd = (x) => (2 * x + 1) % 2;

const QuoteSlides = ({ quotesList, isMobile }: QuoteSlidesProps) => {
  const carouselContext = useContext(CarouselContext);
  const [currentSlide, setCurrentSlide] = useState(carouselContext.state.currentSlide);

  useEffect(() => {
    const onChange = () => setCurrentSlide(carouselContext.state.currentSlide);
    carouselContext.subscribe(onChange);
    return () => carouselContext.unsubscribe(onChange);
  }, [currentSlide]);

  const DotsComponent = ({ isMobile }: DotsComponentProps) => {
    if (isMobile) {
      return (
        <>
          {quotesList.map((quoteElem, index) => (
            <Dot
              key={quoteElem.key}
              slide={index}
              className={`${styles.dot} ${index !== currentSlide && styles.selected}`}
            />
          ))}
        </>
      );
    }
    return null;
  };

  return (
    <>
      <Slider
        className={`${isMobile ? styles.customSlider : ''} ${
          quotesList?.length === 2 && !isMobile ? styles.twoElementCenterContainer : ''
        }`}
        classNameTray={styles.sliderTray}>
        {quotesList.map((quoteElem, index) => (
          <Slide
            key={quoteElem.key}
            index={index}
            className={styles.centerSlide}
            innerClassName={`${styles.slide} ${isOdd(index) ? styles.notRecommended : ''} ${
              quotesList?.length === 2 && !isMobile ? styles.twoElementCentered : ''
            }`}>
            {quoteElem}
          </Slide>
        ))}
      </Slider>
      <DotsComponent isMobile={isMobile} />
    </>
  );
};

const DirectionalBtn = ({ componentName, isMobile, iconClassName, className }) => {
  if (isMobile) {
    const ButtonComponent = carouselComponents[componentName];
    return (
      <ButtonComponent className={className}>
        <Icon {...chevronIcon} className={iconClassName} />
      </ButtonComponent>
    );
  }
  return null;
};

const CarouselComponent = ({ isMobile, children, quotesList }) => {
  const currentSlide = isMobile ? 1 : 0;
  return (
    <CarouselProvider
      naturalSlideWidth={100}
      naturalSlideHeight={130}
      isIntrinsicHeight
      totalSlides={quotesList.length}
      currentSlide={currentSlide}
      visibleSlides={visibleSlides(isMobile)}
      touchEnabled={isMobile}
      dragEnabled={isMobile}>
      {children}
    </CarouselProvider>
  );
};

const QuoteList = ({
  quoteOptions,
  onQuoteSelection,
  onCustomQuoteSelection,
  recommendedQuote,
  recommendedQuoteText,
  texts,
  currency = 'GBP',
  locale,
  tooltipFillColor,
  dataCyQuoteList,
  sectionProps,
  quoteMessagesQuoteList,
}: QuoteListProps) => {
  if (!texts) return null;

  const isMobile = useCheckMobileScreen();

  const quotesList = quoteOptions?.map((quoteOption, idx: number) => (
    <QuoteItem
      key={`${quoteOption.split}${quoteOption.description}`}
      quoteOption={quoteOption}
      onQuoteSelection={onQuoteSelection}
      onCustomQuoteSelection={onCustomQuoteSelection}
      recommendedQuote={recommendedQuote}
      recommendedQuoteText={recommendedQuoteText}
      texts={texts}
      currency={currency}
      locale={locale}
      tooltipFillColor={tooltipFillColor}
      dataCyQuoteListItem={dataCyQuoteList?.[idx]}
      sectionProps={sectionProps?.[idx]}
    />
  ));

  return (
    <div data-testid='QuoteList' className={styles.quoteList}>
      <CarouselComponent isMobile={isMobile} quotesList={quotesList}>
        <DirectionalBtn
          componentName='ButtonBack'
          className={`${styles.button} ${styles.backButton}`}
          iconClassName={styles.backArrow}
          isMobile={isMobile}
        />
        <DirectionalBtn
          componentName='ButtonNext'
          className={`${styles.button} ${styles.nextButton}`}
          iconClassName={styles.nextArrow}
          isMobile={isMobile}
        />
        <QuoteSlides quotesList={quotesList} isMobile={isMobile} />
      </CarouselComponent>
      {quoteMessagesQuoteList && <div className={styles.starText}>{quoteMessagesQuoteList}</div>}
    </div>
  );
};

export default QuoteList;
